import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ArtworkModel, MappedArtworkPageDataChange } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'
import type { CreateArtworkWithCampaignParams } from '@hooks/useArtwork'

import { clearArtworkPageDataChanges } from './artworkPage'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/artwork/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/artwork/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/artwork/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/artwork/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/artwork/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/artwork/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/artwork/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/artwork/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/artwork/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/artwork/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/artwork/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/artwork/DELETE_FAILURE'

export type ArtworkModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: ArtworkModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadArtworks(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().artworks.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/artworks.json', options)
      .then(({ data }: { data: ArtworkModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadArtwork(artworkId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.artworks.loadedForKeys]
    const loadedIds = [...state.artworks.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (artworkId && !loadedIds.includes(artworkId)){
      loadedIds.push(artworkId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/artworks/${artworkId}.json`, options)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadArtworkDataChanges(
  artworkId: number,
  options: EntityOptions = {},
) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/artworks/${artworkId}/data_changes.json`, options)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createArtwork(
  artwork: Partial<ArtworkModel> & { brandTemplateId?: string, brand_template_id?: string },
  options: EntityOptions = {},
) {
  const { brandTemplateId, ...restArtwork } = artwork

  const config = {
    method: 'POST',
    data: JSON.stringify({
      artwork: {
        ...restArtwork,
        artwork_pages: [{}],
        canva_brand_template_id: brandTemplateId,
      },
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/artworks.json', options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createArtworkWithCampaign(params: CreateArtworkWithCampaignParams, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(params),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/artworks/create_with_campaign.json', options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

export function updateArtwork(
  artwork: UpdateParams<ArtworkModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      artwork,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/artworks/${artwork.id}.json`, options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function approveArtwork(
  artworkId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/artworks/${artworkId}/approve.json`, options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function unapproveArtwork(
  artworkId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/artworks/${artworkId}/unapprove.json`, options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function patchArtworkData(
  artworkId: number,
  changes: MappedArtworkPageDataChange[],
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      changes: JSON.stringify(changes),
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/artworks/${artworkId}/patch_data.json`, options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        // Clear Data Changes
        changes.forEach((change) => {
          const { artwork_page_id } = change
          console.log('clearArtworkPageDataChanges', artwork_page_id)
          dispatch(clearArtworkPageDataChanges(artwork_page_id))
        })

        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function uploadArtwork(
  artworkParams: {
    id: number,
    artwork_file: object,
    campaign_id: number,
    order_item_id: number,
  },
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...artworkParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/artworks/upload.json', {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteArtwork(artwork: DeleteParams<ArtworkModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/artworks/${artwork.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createArtworkFromBackup(orderItemId: number, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order_item_id: orderItemId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/artworks/restore_from_backup.json', options, config)
      .then(({ data }: { data: ArtworkModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function hydrateArtwork(artworkJson: Partial<ArtworkModel>) {
  if (artworkJson.id){
    const normalizedJson = normalize(artworkJson, Schemas.ARTWORK)

    return (dispatch: AppDispatch) => {
      dispatch(updateEntities(normalizedJson))

      // Patch the orderItem so artwork_id is set, allowing useRelations to resolve artwork correctly
      if (artworkJson.order_item_id){
        dispatch(updateEntities({
          entities: {
            // @ts-expect-error TS2345 - partial entity patch, entities reducer handles merging
            orderItems: {
              [artworkJson.order_item_id]: { artwork_id: artworkJson.id },
            },
          },
        }))
      }
    }
  }

  return (dispatch: AppDispatch) => {
    dispatch(fetchFailure(['Could not hydrate undefined artwork.']))
  }
}

export function removeArtwork(artworkId: number) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { artworks } = state.entities

    const artwork = artworks[artworkId]

    if (artwork){
      const normalizedJson = normalize(artwork, Schemas.ARTWORK)
      dispatch(deleteEntity(normalizedJson))
      dispatch(deleteSuccess())
    }
  }
}

// Reducer
export default function reducer(
  state: ArtworkModuleState = initialState,
  action: Action = { type: '' },
): ArtworkModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
